/**
 * Team Inbox — APPROVED WhatsApp template registry (portal side).
 *
 * The out-of-window composer can only send a Meta-APPROVED template. The
 * SERVER is the source of truth for WHICH templates are approved + eligible
 * (`waListConversations` returns `approvedTemplates:[{name,messageType,
 * language}]` from waClientSend.approvedSet()). This module adds the PORTAL's
 * presentation layer for those names: the body text (so we can preview what
 * the client will receive) and a friendly variable label per `{{n}}`.
 *
 * KEEP IN SYNC with functions/lib/waTemplateBodies.js (the bodies) and
 * functions/lib/waClientSend.js CLOUD_TEMPLATES (the names). Bodies are
 * AV-INTENT copy (verified:false) until reconciled against the Meta template
 * manager — the picker shows a "preview (approx.)" note so staff know.
 *
 * A template the server returns but that is NOT in this registry still sends
 * (the send path persists the server-rendered body); the picker just shows a
 * generic preview ("This approved template will be sent.") + numbered inputs.
 */

export interface TemplateMeta {
  /** Body text with `{{1}}`, `{{2}}`… 1-indexed placeholders. */
  body: string;
  /** Friendly label per variable index (variables[0] → {{1}}). */
  variables: string[];
}

/**
 * Body + variable labels for the known approved templates. Names match the
 * 7121 `_v3` Meta templates (2026-06-21 cutover) + OTP + intake link.
 */
export const TEMPLATE_REGISTRY: Record<string, TemplateMeta> = {
  av_client_login_otp: {
    body: 'Your Aayatan Veda login code is {{1}}. It is valid for 10 minutes. Do not share it with anyone.',
    variables: ['OTP code'],
  },
  av_client_portal_welcome_v3: {
    body: 'Hi {{1}}, welcome to Aayatan Veda. Access your client portal here: {{2}}',
    variables: ['Client first name', 'Portal link'],
  },
  av_lead_ack_v3: {
    body: 'Hi {{1}}, thank you for contacting Aayatan Veda. We have received your enquiry and our team will get back to you shortly.',
    variables: ['First name'],
  },
  av_quote_payment_v3: {
    body: 'Hi {{1}}, your Aayatan Veda quotation for {{2}} is ready. Amount: {{3}}. To confirm, please complete the payment here: {{4}} (Ref {{5}}). {{6}} {{7}}',
    variables: [
      'First name',
      'Service',
      'Amount',
      'Payment link',
      'Reference',
      'Note 1',
      'Note 2',
    ],
  },
  av_quote_manual_v3: {
    body: 'Hi {{1}}, your Aayatan Veda quotation for {{2}} is ready. Amount: {{3}}. Reference {{4}}. {{5}}',
    variables: ['First name', 'Service', 'Amount', 'Reference', 'Note'],
  },
  av_quote_accepted_payment_v3: {
    body: 'Hi {{1}}, thank you for accepting your Aayatan Veda quotation. Please complete the payment to begin: {{2}}',
    variables: ['First name', 'Payment link'],
  },
  av_quote_accepted_manual_v3: {
    body: 'Hi {{1}}, thank you for accepting your Aayatan Veda quotation for {{2}} (Ref {{3}}). {{4}}',
    variables: ['First name', 'Service', 'Reference', 'Note'],
  },
  av_intake_link: {
    body: 'Hi {{1}}, please complete your Aayatan Veda intake form here: {{2}}',
    variables: ['First name', 'Intake link'],
  },
  av_payment_reminder_gentle: {
    body: 'Hi {{1}}, a gentle reminder — an amount of {{2}} is pending for your Aayatan Veda service. You can pay here: {{3}}',
    variables: ['First name', 'Outstanding amount', 'Payment link'],
  },
};

/** Count of distinct `{{n}}` placeholders referenced in a body. */
export function placeholderCount(body: string): number {
  const seen = new Set<number>();
  const re = /\{\{(\d+)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) seen.add(Number(m[1]));
  return seen.size ? Math.max(...seen) : 0;
}

/**
 * PURE — resolve the body + variable labels for a template NAME. Falls back to
 * a generic shape for an unknown (server-approved but unregistered) template:
 * a placeholder-free body and zero labelled variables.
 */
export function templateMeta(name: string): TemplateMeta {
  const hit = TEMPLATE_REGISTRY[name];
  if (hit) return hit;
  return { body: '', variables: [] };
}

/**
 * PURE — fill ordered params into a `{{n}}` body for the live preview. An
 * unsupplied/blank param leaves a soft `‹label›` hint so staff see the gap.
 */
export function fillTemplateBody(
  body: string,
  params: string[],
  variables: string[] = [],
): string {
  if (!body) return '';
  return body.replace(/\{\{(\d+)\}\}/g, (_m, n) => {
    const idx = Number(n) - 1;
    const v = params[idx];
    if (v != null && v !== '') return String(v);
    const label = variables[idx];
    return label ? `‹${label}›` : `‹{{${n}}}›`;
  });
}
